import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Badge, Button, Popover, Typography, Tag, Flex, Empty, Spin, Tooltip, Tabs } from "antd";
import {
  Notification02Icon,
  ReloadIcon,
  ArrowRight01Icon,
  Call02Icon,
  Video01Icon,
  Task01Icon,
  Calendar03Icon,
  Cancel01Icon,
} from "hugeicons-react";
import { getStagnantDeals } from "../../../services/pipelineService";
import { getTodayActivities } from "../../../services/activityService";
import { getTodayBreaks } from "../../../services/breakService";
import useAuthSession from "../../../hooks/useAuthSession";

const { Text } = Typography;

const ACTIVITY_TYPE_CONFIG = {
  call: {
    label: "Call",
    color: "blue",
    icon: <Call02Icon size={12} color="#2563eb" />,
  },
  meeting: {
    label: "Meeting",
    color: "purple",
    icon: <Video01Icon size={12} color="#7c3aed" />,
  },
  task: {
    label: "Task",
    color: "cyan",
    icon: <Task01Icon size={12} color="#0891b2" />,
  },
};

/**
 * TopNavbarNotifications Component
 *
 * Shows:
 * 1. Deals in the same pipeline stage for more than 7 days
 * 2. Scheduled activities/tasks/meetings for Today
 * 3. Daily break limit violation alerts (for Admin when sales rep pause exceeds threshold)
 */
function TopNavbarNotifications({ companyCode: propCompanyCode, employeeId: propEmployeeId, isEmployee: propIsEmployee, onNavigateTab }) {
  const session = useAuthSession();
  const effectiveCompanyCode = propCompanyCode || session.companyCode;
  const effectiveEmployeeId = propEmployeeId || session.employeeId;
  const effectiveIsEmployee = propIsEmployee !== undefined ? propIsEmployee : session.isEmployee;

  const [stagnantDeals, setStagnantDeals] = useState([]);
  const [todayActivities, setTodayActivities] = useState([]);
  const [breakAlerts, setBreakAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState("activities");
  const [isOpen, setIsOpen] = useState(false);
  const isMountedRef = useRef(true);

  const getStorageKey = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    return `dealvoice_dismissed_notifications_${effectiveCompanyCode || "default"}_${today}`;
  }, [effectiveCompanyCode]);

  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const saved = localStorage.getItem(`dealvoice_dismissed_notifications_${effectiveCompanyCode || "default"}_${today}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  const handleDismiss = useCallback((id) => {
    if (!id) return;
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(String(id));
      try {
        localStorage.setItem(getStorageKey(), JSON.stringify(Array.from(next)));
      } catch (e) {
        console.error("Failed to save dismissed notification", e);
      }
      return next;
    });
  }, [getStorageKey]);

  const handleClearAll = useCallback(() => {
    const allIds = [
      ...todayActivities.map((a) => String(a._id)),
      ...stagnantDeals.map((d) => String(d._id)),
      ...breakAlerts.map((b) => String(b.employeeId || b._id)),
    ];
    setDismissedIds((prev) => {
      const next = new Set(prev);
      allIds.forEach((id) => next.add(id));
      try {
        localStorage.setItem(getStorageKey(), JSON.stringify(Array.from(next)));
      } catch (e) {
        console.error("Failed to save dismissed notifications", e);
      }
      return next;
    });
  }, [todayActivities, stagnantDeals, breakAlerts, getStorageKey]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchAllNotifications = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setLoading(true);
    try {
      const dealOptions = { days: 7 };
      const actOptions = {};
      if (effectiveIsEmployee && effectiveEmployeeId) {
        dealOptions.employeeId = effectiveEmployeeId;
        actOptions.employeeId = effectiveEmployeeId;
      }

      const promises = [
        getStagnantDeals(effectiveCompanyCode, dealOptions),
        getTodayActivities(effectiveCompanyCode, actOptions),
      ];

      // If Admin, also fetch break violations
      if (!effectiveIsEmployee) {
        promises.push(getTodayBreaks(effectiveCompanyCode));
      }

      const [dealsRes, actRes, breaksRes] = await Promise.all(promises);

      if (isMountedRef.current) {
        if (dealsRes?.success && Array.isArray(dealsRes.deals)) {
          setStagnantDeals(dealsRes.deals);
        }
        if (actRes?.success && Array.isArray(actRes.data)) {
          setTodayActivities(actRes.data);
        }
        if (breaksRes?.success && Array.isArray(breaksRes.overLimit)) {
          setBreakAlerts(breaksRes.overLimit);
        } else if (effectiveIsEmployee) {
          setBreakAlerts([]);
        }
      }
    } catch (err) {
      console.error("fetchAllNotifications error:", err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [effectiveCompanyCode, effectiveEmployeeId, effectiveIsEmployee]);

  useEffect(() => {
    fetchAllNotifications();
    const interval = setInterval(fetchAllNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchAllNotifications]);

  const handleSelectDeal = (deal) => {
    setIsOpen(false);
    if (onNavigateTab) {
      onNavigateTab("pipeline", deal);
    }
  };

  const handleSelectActivity = (activity) => {
    setIsOpen(false);
    if (onNavigateTab) {
      onNavigateTab("activities", activity);
    }
  };

  const formatActivityTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const visibleActivities = useMemo(
    () => todayActivities.filter((a) => !dismissedIds.has(String(a._id))),
    [todayActivities, dismissedIds]
  );
  const visibleDeals = useMemo(
    () => stagnantDeals.filter((d) => !dismissedIds.has(String(d._id))),
    [stagnantDeals, dismissedIds]
  );
  const visibleBreaks = useMemo(
    () => breakAlerts.filter((b) => !dismissedIds.has(String(b.employeeId || b._id))),
    [breakAlerts, dismissedIds]
  );

  const totalCount = visibleDeals.length + visibleActivities.length + visibleBreaks.length;

  const tabItems = [
    {
      key: "activities",
      label: (
        <span style={{ fontSize: "0.8rem" }}>
          Today Activities{" "}
          {visibleActivities.length > 0 && (
            <Badge count={visibleActivities.length} size="small" style={{ backgroundColor: "#2563eb", marginLeft: 4 }} />
          )}
        </span>
      ),
    },
    {
      key: "deals",
      label: (
        <span style={{ fontSize: "0.8rem" }}>
          Stuck Deals{" "}
          {visibleDeals.length > 0 && (
            <Badge count={visibleDeals.length} size="small" style={{ backgroundColor: "#e11d48", marginLeft: 4 }} />
          )}
        </span>
      ),
    },
  ];

  if (!effectiveIsEmployee) {
    tabItems.push({
      key: "breaks",
      label: (
        <span style={{ fontSize: "0.8rem" }}>
          Break Alerts{" "}
          {visibleBreaks.length > 0 && (
            <Badge count={visibleBreaks.length} size="small" style={{ backgroundColor: "#d97706", marginLeft: 4 }} />
          )}
        </span>
      ),
    });
  }

  const notificationContent = (
    <div style={{ width: 370, maxHeight: 500, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Flex justify="space-between" align="center" style={{ padding: "10px 14px 6px", borderBottom: "1px solid #f1f5f9" }}>
        <Flex vertical>
          <Flex align="center" gap={6}>
            <Text strong style={{ fontSize: "0.95rem", color: "#0f172a" }}>
              Notifications
            </Text>
          </Flex>
          <Text type="secondary" style={{ fontSize: "0.72rem" }}>
            Tasks, deals &amp; sales rep break alerts
          </Text>
        </Flex>

        <Flex align="center" gap={8}>
          {totalCount > 0 && (
            <Button
              type="link"
              size="small"
              onClick={handleClearAll}
              style={{ fontSize: "0.72rem", color: "#64748b", padding: 0 }}
            >
              Clear All
            </Button>
          )}
          <Tooltip title="Refresh notifications">
            <Button
              type="text"
              size="small"
              icon={<ReloadIcon size={14} color="#64748b" className={loading ? "anticon-spin" : ""} />}
              onClick={fetchAllNotifications}
            />
          </Tooltip>
        </Flex>
      </Flex>

      {/* Tabs */}
      <div style={{ padding: "0 12px" }}>
        <Tabs
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          size="small"
          style={{ marginBottom: 0 }}
          items={tabItems}
        />
      </div>

      {/* Content List */}
      <div style={{ flex: 1, overflowY: "auto", maxHeight: 310, padding: "8px 12px" }}>
        {loading && totalCount === 0 ? (
          <Flex justify="center" align="center" style={{ padding: "30px 0" }}>
            <Spin size="small" />
          </Flex>
        ) : activeTabKey === "activities" ? (
          visibleActivities.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Flex vertical align="center" gap={2}>
                  <Text strong style={{ fontSize: "0.85rem", color: "#334155" }}>
                    No pending activities for today
                  </Text>
                  <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                    You are all caught up with scheduled tasks!
                  </Text>
                </Flex>
              }
              style={{ margin: "20px 0" }}
            />
          ) : (
            <Flex vertical gap={4}>
              {visibleActivities.map((act, idx) => {
                const typeCfg = ACTIVITY_TYPE_CONFIG[act.type] || ACTIVITY_TYPE_CONFIG.task;
                const formattedTime = formatActivityTime(act.activityDate);

                return (
                  <div
                    key={act._id}
                    onClick={() => handleSelectActivity(act)}
                    style={{
                      padding: "8px 4px",
                      cursor: "pointer",
                      borderBottom: idx < visibleActivities.length - 1 ? "1px solid #f1f5f9" : "none",
                      transition: "all 0.2s ease",
                    }}
                    className="topbar-notification-item"
                  >
                    <Flex justify="space-between" align="start" gap={8}>
                      <Flex vertical style={{ minWidth: 0, flex: 1 }}>
                        <Flex align="center" gap={6}>
                          <span style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600 }}>
                            {typeCfg.label}
                          </span>
                          <Text strong style={{ fontSize: "0.85rem", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {act.title}
                          </Text>
                        </Flex>
                        {(act.leadCompanyName || act.contactName) && (
                          <Text style={{ fontSize: "0.74rem", color: "#64748b", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {act.leadCompanyName || act.contactName}
                          </Text>
                        )}
                      </Flex>

                      <Flex align="center" gap={6}>
                        {formattedTime && (
                          <Text style={{ fontSize: "0.72rem", color: "#64748b", whiteSpace: "nowrap" }}>
                            {formattedTime}
                          </Text>
                        )}
                        <Tooltip title="Dismiss">
                          <Button
                            type="text"
                            size="small"
                            icon={<Cancel01Icon size={12} color="#94a3b8" />}
                            style={{ width: 18, height: 18, minWidth: 18, padding: 0 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDismiss(act._id);
                            }}
                          />
                        </Tooltip>
                      </Flex>
                    </Flex>
                  </div>
                );
              })}
            </Flex>
          )
        ) : activeTabKey === "deals" ? (
          visibleDeals.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Flex vertical align="center" gap={2}>
                  <Text strong style={{ fontSize: "0.85rem", color: "#334155" }}>
                    All deals are moving smoothly!
                  </Text>
                  <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                    No deals have been in the same stage for more than 7 days.
                  </Text>
                </Flex>
              }
              style={{ margin: "20px 0" }}
            />
          ) : (
            <Flex vertical gap={4}>
              {visibleDeals.map((deal, idx) => (
                <div
                  key={deal._id}
                  onClick={() => handleSelectDeal(deal)}
                  style={{
                    padding: "8px 4px",
                    cursor: "pointer",
                    borderBottom: idx < visibleDeals.length - 1 ? "1px solid #f1f5f9" : "none",
                    transition: "all 0.2s ease",
                  }}
                  className="topbar-notification-item"
                >
                  <Flex justify="space-between" align="start" gap={8}>
                    <Flex vertical style={{ minWidth: 0, flex: 1 }}>
                      <Text strong style={{ fontSize: "0.85rem", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {deal.dealName}
                      </Text>
                      <Text style={{ fontSize: "0.74rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>
                        {deal.leadCompanyName || "Unknown Company"}
                      </Text>
                    </Flex>
                    <Flex align="center" gap={6}>
                      {deal.amount > 0 && (
                        <Text strong style={{ fontSize: "0.82rem", color: "#0f172a", whiteSpace: "nowrap" }}>
                          ₹{deal.amount.toLocaleString()}
                        </Text>
                      )}
                      <Tooltip title="Dismiss">
                        <Button
                          type="text"
                          size="small"
                          icon={<Cancel01Icon size={12} color="#94a3b8" />}
                          style={{ width: 18, height: 18, minWidth: 18, padding: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDismiss(deal._id);
                          }}
                        />
                      </Tooltip>
                    </Flex>
                  </Flex>

                  <Flex justify="space-between" align="center" style={{ marginTop: 6 }}>
                    <Text
                      style={{
                        fontSize: "0.74rem",
                        fontWeight: 500,
                        color: "#64748b",
                      }}
                    >
                      {deal.stageLabel}
                    </Text>

                    <Text style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 600 }}>
                      {deal.daysInStage}d in stage
                    </Text>
                  </Flex>
                </div>
              ))}
            </Flex>
          )
        ) : visibleBreaks.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Flex vertical align="center" gap={2}>
                <Text strong style={{ fontSize: "0.85rem", color: "#334155" }}>
                  All sales reps within limit!
                </Text>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  No team members have exceeded daily break limits today.
                </Text>
              </Flex>
            }
            style={{ margin: "20px 0" }}
          />
        ) : (
          <Flex vertical gap={4}>
            {visibleBreaks.map((alert, idx) => (
              <div
                key={alert.employeeId || idx}
                style={{
                  padding: "8px 4px",
                  borderBottom: idx < visibleBreaks.length - 1 ? "1px solid #f1f5f9" : "none",
                  transition: "all 0.2s ease",
                }}
                className="topbar-notification-item"
              >
                <Flex justify="space-between" align="start" gap={8}>
                  <Flex vertical style={{ minWidth: 0, flex: 1 }}>
                    <Flex align="center" gap={6}>
                      <span style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600 }}>
                        Break Exceeded
                      </span>
                      <Text strong style={{ fontSize: "0.85rem", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {alert.employeeName || "Sales Rep"}
                      </Text>
                    </Flex>
                    <Text style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 4 }}>
                      Paused for <strong style={{ color: "#0f172a" }}>{alert.totalMinutes || Math.round(alert.totalSeconds / 60)} mins</strong> (Max limit: {alert.limitMinutes || Math.round(alert.limitSeconds / 60)} mins)
                    </Text>
                  </Flex>

                  <Flex align="center" gap={6}>
                    <Tag
                      color="error"
                      style={{
                        fontSize: "0.7rem",
                        borderRadius: 10,
                        margin: 0,
                        fontWeight: 700,
                      }}
                    >
                      +{alert.exceededMinutes || Math.max(0, Math.round((alert.totalSeconds - alert.limitSeconds) / 60))}m
                    </Tag>
                    <Tooltip title="Dismiss">
                      <Button
                        type="text"
                        size="small"
                        icon={<Cancel01Icon size={12} color="#94a3b8" />}
                        style={{ width: 18, height: 18, minWidth: 18, padding: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismiss(alert.employeeId || alert._id);
                        }}
                      />
                    </Tooltip>
                  </Flex>
                </Flex>
              </div>
            ))}
          </Flex>
        )}
      </div>

      {/* Footer */}
      <Flex justify="space-between" align="center" style={{ padding: "8px 14px", borderTop: "1px solid #f1f5f9", background: "#fcfcfd" }}>
        <Button
          type="link"
          size="small"
          onClick={() => {
            setIsOpen(false);
            onNavigateTab?.("activities");
          }}
          style={{ fontSize: "0.75rem", padding: 0, display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          <Calendar03Icon size={13} style={{ verticalAlign: "middle" }} /> Calendar Activities
        </Button>

        <Button
          type="link"
          size="small"
          onClick={() => {
            setIsOpen(false);
            onNavigateTab?.("pipeline");
          }}
          style={{ fontSize: "0.75rem", padding: 0, display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          Sales Pipeline <ArrowRight01Icon size={13} style={{ verticalAlign: "middle" }} />
        </Button>
      </Flex>
    </div>
  );

  return (
    <Popover
      content={notificationContent}
      trigger="click"
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottomRight"
      arrow={{ pointAtCenter: true }}
      styles={{ body: { padding: 0, borderRadius: 12, overflow: "hidden", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)" } }}
    >
      <Tooltip title="Notifications: Today's Tasks & Pipeline Alerts">
        <button
          type="button"
          className={`topbar-notification-btn ${isOpen ? "active" : ""}`}
          style={{
            background: isOpen ? "#f1f5f9" : "none",
            border: "1px solid #e2e8f0",
            borderRadius: "50%",
            width: 34,
            height: 34,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            position: "relative",
            transition: "all 0.15s ease",
            padding: 0,
          }}
          aria-label="Notifications"
        >
          <Badge count={totalCount} overflowCount={99} size="small" offset={[2, -2]}>
            <Notification02Icon size={18} color={totalCount > 0 ? "#e11d48" : "#64748b"} style={{ display: "block" }} />
          </Badge>
        </button>
      </Tooltip>
    </Popover>
  );
}

export default TopNavbarNotifications;
